import { BCMSRecord, BCMSStateManagerCRUD, useBCMSContext, useModel, useStateManager } from "@bcm-dev/bcms-vite";
import ExternalIframe from 'components/shared/containers/ExternalIframe';
import PageContainer from 'components/shared/containers/PageContainer';
import ContentHeaderView from 'components/shared/views/ContentHeaderView';
import ContentSubheaderView from 'components/shared/views/ContentSubheaderView';
import ProfilePicker from 'components/shared/widgets/ProfilePicker';
import Classes, { Profile } from "model";
import React, { useEffect } from 'react';
import "./mobile.css";
import "./styles.css";
import TransparentPageContentView from "./components/TransparentPageContentView";

/**
 * @function TransparentIFramePageContainer
 * @return {React.Component}
 * @note - Branding for this page is being added to the window object so captures can utilize the set value in state code
 * @exported true
 * @description The main component for PaymentsPage  
 */
export default function TransparentIFramePageContainer(props) {
  const model = useModel();
  const context = useBCMSContext();

  const { defaultView } = props ?? {};

  BCMSRecord.globalControllerFor(Classes, context);

  // Branding options needed for captures
  const { options } = model.branding ?? {};
  const { cashflow_central_abi22: cfcEnabled, cfc25_courseID_abi22: cfcCourse } = options ?? {};

  const cfcIsValid = cfcEnabled === "true" && cfcCourse !== null && cfcCourse !== "";

  const data = getDefaultPageData();
  const ui = getDefaultPageUI(options, defaultView);

  window.BRANDING = { "rxp17_abi22": options.rxp17_abi22 };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    options,
    demos: model.demos,
    cfcIsValid,

    onChange: value => {
      BCMSStateManagerCRUD.init(state)
        .update(["data", "selected"], value)
        .submit(setState);
    },

    onSelect: evt => {
      const profile = Profile.initByID(state.data.selected),
        isBusiness = profile.type === "business";

      BCMSStateManagerCRUD.init(state)
        .update(["ui", "showCFC"], cfcIsValid && isBusiness)
        .submit(setState);
    }
  });

  return (
    <PageContainer className="transparent-iframe-page-container integrated-product-page" ignoreTrigger={true} state={{ data, ui }} controller={controller}>
      <PageContent />
    </PageContainer>
  );
}

const getDefaultPageData = () => ({
  "selected": Profile.firstInstanceOf().id
});

const getDefaultPageUI = (options, defaultView) => {
  const profile = Profile.firstInstanceOf(),
    isBusiness = profile.type === "business",

    showCFC = (defaultView !== undefined && defaultView === "cfc25") ||
      (options.cashflow_central_abi22 === "true" && (options.cfc25_courseID_abi22 !== "" && options.cfc25_courseID_abi22 !== null) && isBusiness);

  return {
    "showCFC": showCFC
  };
}

function PageContent(props) {
  const { options, demos, ui, data, onChange, onSelect, cfcIsValid } = useStateManager() ?? {};
  const iframeContainerRef = React.useRef(null);
  const centerContentRef = React.useRef(null);
  const backgroundCutoutRef = React.useRef(null);
  const gradientCutoutRef = React.useRef(null);
  const centerContentCutoutRef = React.useRef(null);

  const label = options.payments_label_abi22 ?? "Payments";
  const paymentDemo = demos.RXP17 ? "RXP17" : "RXP22";

  const subheaderText = options.payments_subheader_abi22;
  const hasSubheaderText = subheaderText ? true : false;
  const hasProfilePicker = options.multiprofile_abi22 === "true";

  const mabiBranding = window.mabi23Branding;
  const option = mabiBranding
    ? mabiBranding.options.integrated_page_header_mabi23
    : options.integrated_page_header_abi22;
  const showHeader = option !== "false";

  useEffect(() => {
    const setTranparentWindow = () => {
      if (!iframeContainerRef.current || !centerContentRef.current || !backgroundCutoutRef.current || !gradientCutoutRef.current || !centerContentCutoutRef.current) {
        return;
      }

      const iframeRect = iframeContainerRef.current.getBoundingClientRect();

      [backgroundCutoutRef.current, gradientCutoutRef.current, centerContentCutoutRef.current].forEach(cutoutNode => {
        const cutoutRect = cutoutNode.getBoundingClientRect();

        const cutoutTop = Math.max(0, iframeRect.top - cutoutRect.top);
        const cutoutLeft = Math.max(0, iframeRect.left - cutoutRect.left);
        const cutoutWidth = Math.max(0, Math.min(iframeRect.width, cutoutRect.width - cutoutLeft));
        const cutoutHeight = Math.max(0, Math.min(iframeRect.height, cutoutRect.height - cutoutTop));
        const rightWidth = Math.max(0, cutoutRect.width - cutoutLeft - cutoutWidth);
        const bottomHeight = Math.max(0, cutoutRect.height - cutoutTop - cutoutHeight);

        cutoutNode.style.padding = `${cutoutTop}px ${rightWidth}px ${bottomHeight}px ${cutoutLeft}px`;
      });
    };
    const timer = setTimeout(() => {
      setTranparentWindow();
    }, 1000);

    window.addEventListener("resize", setTranparentWindow);

    return () => {
      window.removeEventListener("resize", setTranparentWindow);
      clearTimeout(timer);
    };
  }, []);

  return (
    <TransparentPageContentView >
      <div ref={gradientCutoutRef} className="linear-gradient-bg-cutout"></div>
      <div ref={backgroundCutoutRef} className="background-image-cutout"></div>
      {options.multiprofile_abi22 === "true" &&
        <ProfilePicker profiles={Profile.initAll()} ui={{}} onChange={onChange} onSelectClick={onSelect} disableBusiness={!cfcIsValid}
          profileID={data.selected} />}

      <div ref={centerContentRef} className="flex-container flex-vertical flex-width center-content">
        <div ref={centerContentCutoutRef} className="center-content-cutout"></div>
        { showHeader && <ContentHeaderView text={label}/> }

        {hasSubheaderText && <ContentSubheaderView text={subheaderText} />}
        <div ref={iframeContainerRef} className={`iframe-container flex-container flex-horizontal justify-center ${ui.showCFC ? "cfc25-iframe-active" : ""}`}>
          {/* iFrame for RXP17 goes here */}
          <ExternalIframe className="payments-iframe cfc25-iframe" demo="CFC25" key="PaymentsPage_CFC" /> 
        </div>
      </div>
    </TransparentPageContentView>
  );
}
